import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Divider,
  Icon,
  Label,
  Message,
} from 'semantic-ui-react';
import { useDispatch } from 'react-redux';
import updateTransactionStatus from 'redux/actions/groups/updateTransactionStatus';
import CreditArrow from 'assets/images/credit-wallet-arrow.svg';
import DebitArrow from 'assets/images/debit-wallet-arrow.svg';
import './index.scss';

const statusColors = {
  success: '#029B5d',
  failed: '#f04043',
  pending: '#aa5E00',
};

const TransactionDetailsPopup = ({
  transaction,
  onTransactionRefresh,
}) => {
  const dispatch = useDispatch();
  const [refreshing, setRefreshing] = useState(false);

  const {
    id,
    type,
    userId,
    groupId,
    providerTid,
    status: rawStatus,
    comment,
    responseErrorMessage,
  } = transaction;

  const status = rawStatus?.trim();

  const fields = [
    {
      label: 'Transaction type',
      value: (
        <span className="transaction-details-popup__value--type">
          <img
            alt={type}
            src={type === 'credit' ? CreditArrow : DebitArrow}
            className="transaction-details-popup__type-icon"
          />
          {type === 'credit' ? 'Credit' : 'Debit'}
        </span>
      ),
    },
    { label: 'User ID', value: userId },
    { label: 'Group ID', value: groupId },
    { label: 'Transaction ID', value: id },
    { label: 'Provider TID', value: providerTid },
    {
      label: 'Status',
      value: (
        <Label
          size="tiny"
          style={{
            color: statusColors[status],
            backgroundColor: `${statusColors[status]}20`,
          }}
        >
          {status?.toUpperCase()}
        </Label>
      ),
    },
  ];

  const handleRefresh = () => {
    setRefreshing(true);
    updateTransactionStatus(id)(dispatch, () => {
      setRefreshing(false);
      if (onTransactionRefresh) onTransactionRefresh();
    });
  };

  const statusMessage = (() => {
    if (status === 'failed') {
      return (
        <Message negative size="small">
          <Message.Header>Error:</Message.Header>
          <p>
            {responseErrorMessage || comment || 'Transaction failed.'}
          </p>
        </Message>
      );
    }
    if (status === 'pending') {
      return (
        <Message warning size="small">
          <p>
            This transaction is currently being processed. Refresh the
            transaction status to retrieve the latest update
          </p>
        </Message>
      );
    }
    return null;
  })();

  return (
    <div className="transaction-details-popup">
      {fields
        .filter(({ value }) => value)
        .map(({ label, value, valueClass }, index) => (
          <React.Fragment key={label}>
            {index > 0 && <Divider />}
            <div className="transaction-details-popup__row">
              <span className="transaction-details-popup__label">
                {label}
              </span>
              <span
                className={`transaction-details-popup__value${
                  valueClass ? ` ${valueClass}` : ''
                }`}
              >
                {value}
              </span>
            </div>
          </React.Fragment>
        ))}

      {statusMessage && (
        <>
          <Divider />
          {statusMessage}
        </>
      )}

      <Divider />

      <Button
        fluid
        basic
        color="blue"
        loading={refreshing}
        className="transaction-details-popup__refresh-btn"
        onClick={handleRefresh}
      >
        <Icon name="refresh" />
        Refresh transaction status
      </Button>
    </div>
  );
};

TransactionDetailsPopup.propTypes = {
  transaction: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    userId: PropTypes.string,
    groupId: PropTypes.number,
    providerTid: PropTypes.string,
    status: PropTypes.string,
    comment: PropTypes.string,
    responseErrorMessage: PropTypes.string,
  }).isRequired,
  onTransactionRefresh: PropTypes.func,
};

TransactionDetailsPopup.defaultProps = {
  onTransactionRefresh: null,
};

export default TransactionDetailsPopup;
